import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private meta: Meta,
    private title: Title
  ) {}

  /**
   * Update the page title
   */
  updateTitle(title: string) {
    this.title.setTitle(title);
  }

  /**
   * Update meta description
   */
  updateMetaDescription(description: string) {
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
    } else {
      this.meta.removeTag('name="description"');
    }
  }

  /**
   * Update Open Graph tags for social sharing
   */
  updateOgTags(data: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
  }) {
    // Update OG title
    if (data.title) {
      this.meta.updateTag({ property: 'og:title', content: data.title });
    }

    // Update OG description
    if (data.description) {
      this.meta.updateTag({ property: 'og:description', content: data.description });
    }

    // Update OG image
    if (data.image) {
      this.meta.updateTag({ property: 'og:image', content: data.image });
    }

    // Update OG URL
    if (data.url) {
      this.meta.updateTag({ property: 'og:url', content: data.url });
    }

    // Update OG type
    this.meta.updateTag({ property: 'og:type', content: data.type || 'website' });
  }

  /**
   * Update Twitter Card tags
   */
  updateTwitterTags(data: {
    title?: string;
    description?: string;
    image?: string;
    card?: string;
  }) {
    // Set card type
    this.meta.updateTag({ name: 'twitter:card', content: data.card || 'summary_large_image' });

    // Update Twitter title
    if (data.title) {
      this.meta.updateTag({ name: 'twitter:title', content: data.title });
    }

    // Update Twitter description
    if (data.description) {
      this.meta.updateTag({ name: 'twitter:description', content: data.description });
    }

    // Update Twitter image
    if (data.image) {
      this.meta.updateTag({ name: 'twitter:image', content: data.image });
    }
  }

  /**
   * Update all SEO tags for a content page
   */
  updatePageSeo(data: {
    title: string;
    metaTitle?: string;
    metaDescription?: string;
    ogImageUrl?: string;  // Optional - not used by content pages
    url?: string;
  }) {
    // Use metaTitle if provided, otherwise use title
    const pageTitle = data.metaTitle || data.title;
    
    // Update page title
    this.updateTitle(pageTitle);

    // Update meta description
    if (data.metaDescription) {
      this.updateMetaDescription(data.metaDescription);
    }

    // Update Open Graph tags
    this.updateOgTags({
      title: pageTitle,
      description: data.metaDescription,
      image: data.ogImageUrl,
      url: data.url,
      type: 'website'
    });

    // Update Twitter Card tags
    this.updateTwitterTags({
      title: pageTitle,
      description: data.metaDescription,
      image: data.ogImageUrl,
      card: 'summary_large_image'
    });
  }

  /**
   * Clear all custom meta tags (useful when navigating away from dynamic pages)
   */
  clearMetaTags() {
    // Remove custom meta tags but keep essential ones
    const tagsToRemove = [
      'name="description"',
      'property="og:title"',
      'property="og:description"',
      'property="og:image"',
      'property="og:url"',
      'name="twitter:title"',
      'name="twitter:description"',
      'name="twitter:image"'
    ];

    tagsToRemove.forEach(selector => {
      this.meta.removeTag(selector);
    });
  }

  /**
   * Set default SEO tags for the application
   */
  setDefaultSeo(defaults: {
    title: string;
    description?: string;
    image?: string;
  }) {
    this.updateTitle(defaults.title);
    
    if (defaults.description) {
      this.updateMetaDescription(defaults.description);
    }

    this.updateOgTags({
      title: defaults.title,
      description: defaults.description,
      image: defaults.image,
      type: 'website'
    });

    this.updateTwitterTags({
      title: defaults.title,
      description: defaults.description,
      image: defaults.image
    });
  }
}